import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Shield, Award, CheckCircle, Home, Phone, Mail, MapPin } from 'lucide-react';
import SEOHead from '../../components/SEO/SEOHead';
import PageHeader from '../../components/UI/PageHeader';
import Container from '../../components/UI/Container';
import SectionHeader from '../../components/UI/SectionHeader';
import Button from '../../components/UI/Button';

const About: React.FC = () => {
  return (
    <>
      <SEOHead
        title="Despre Noi"
        description="Protecția Animalelor și Naturii - serviciu în cadrul Primăriei Sectorului 1. Promovăm adopții responsabile și asigurăm bunăstarea animalelor."
        keywords="protecția animalelor, sector 1, adopții, adăpost odăi, câini, animale, bucurești"
      />
      
      <PageHeader
        title="Despre noi"
        subtitle="Fii parte din schimbare. Salvează un suflet."
        image="https://images.pexels.com/photos/406014/pexels-photo-406014.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />

      <Container>
        {/* Cine suntem */}
        <SectionHeader 
          title="Cine suntem?" 
          centered={false}
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:pt-8"
          >
            <p className="text-gray-700 mb-6 text-lg leading-relaxed">
              <strong>Protecția Animalelor și Naturii</strong> este un serviciu nou înființat în cadrul 
              Primăriei Sectorului 1 al Municipiului București. Misiunea noastră este să venim în ajutorul celor care au cea mai mare nevoie de noi.
            </p>
            <p className="text-gray-700 mb-6 leading-relaxed">
              <strong>Cum facem asta? </strong> Prin promovarea continuă a adopțiilor responsabile pentru cei aproximativ 550 de căței din Adăpostul Odăi, asigurarea bunăstării animalelor, demararea campaniilor de microcipare și sterilizare gratuite, și educarea comunității prin organizarea de campanii de prevenție, informare și schimbare a mentalității colective a cetățenilor Sectorului 1. 
            </p>
            <p className="text-gray-700 mb-6 leading-relaxed">
            Ne dorim ca oamenii să realizeze că sunt direct responsabili de problema animalelor fără stăpân și că această problemă pleacă din curțile noastre. Abandonul și iresponsabilitatea nu sunt o soluție. Eliminarea animalelor pentru propriul nostru bine nu este o soluție. 
            Într-o comunitate normală și civilizată, toate formele de viață trebuie respectate. De aceea, considerăm că este o luptă comună pe care trebuie să o ducem pentru a crea o lume mai bună.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Statistics Cards */}
            <div className="grid grid-cols-2 gap-4 mb-2">
              <div className="bg-gradient-to-br from-primary/10 to-accent/10 p-6 rounded-xl text-center shadow-sm">
                <div className="text-3xl font-bold text-primary mb-2">550+</div>
                <div className="text-sm text-gray-600">Căței în îngrijire</div>
              </div>
              <div className="bg-gradient-to-br from-accent/10 to-success/10 p-6 rounded-xl text-center shadow-sm">
                <div className="text-3xl font-bold text-accent mb-2">100%</div>
                <div className="text-sm text-gray-600">Adopții gratuite</div>
              </div>
            </div>

            {/* Key Points & Call to Action */}
            <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl p-6 shadow-sm">
              <div className="space-y-3 mb-6">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-primary rounded-full mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700 font-medium">Serviciu public înființat în 2024</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700 font-medium">Parteneriate cu specialiști veterinari</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-success rounded-full mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700 font-medium">Campanii de educație comunitară</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-warning rounded-full mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700 font-medium">Suport post-adopție continuu</span>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-4 text-center">
                <div className="text-lg font-bold mb-2 text-primary">Alătură-te misiunii noastre!</div>
                <div className="text-sm text-gray-600 mb-4">Fiecare adopție salvează o viață și aduce bucurie în casa ta.</div>
                <Button 
                  href="https://docs.google.com/forms/d/e/1FAIpQLSd09cTbvA9b6a-lxOh-GbnSZ7s2O032iSqNKrDQximxuwXYOA/viewform" 
                  variant="primary" 
                  size="md"
                  external={true}
                >
                  Devino voluntar
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>

      <div className="py-8"></div>

      <Container>
        {/* Proiecte principale */}
        <SectionHeader 
          title="Proiecte principale" 
          subtitle="Activitățile și parteneriatele noastre pentru bunăstarea animalelor"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {[
            {
              icon: <Award size={24} className="text-accent" />,
              title: "Parteneriat cu Facultatea de Medicină Veterinară",
              description: "Susținem formarea practică a viitorilor medici veterinari și oferim îngrijire specializată cățeilor din adăpost"
            },
            {
              icon: <Users size={24} className="text-accent" />,
              title: "Parteneriat cu dresori atestați",
              description: "Pentru socializarea și pregătirea comportamentală a cățeilor"
            },
            {
              icon: <Heart size={24} className="text-accent" />,
              title: "Campanii de adopții",
              description: "Organizăm periodic campanii de adopții pentru a găsi cămine pentru cățeii noștri"
            },
            {
              icon: <Shield size={24} className="text-accent" />,
              title: "Evenimente publice",
              description: "Participăm cu standuri informative la evenimente publice pentru educarea comunității"
            },
            {
              icon: <Users size={24} className="text-accent" />,
              title: "Rețea de voluntari",
              description: "Dezvoltăm o rețea de voluntari implicați în activitățile noastre"
            },
            {
              icon: <CheckCircle size={24} className="text-accent" />,
              title: "Campanii de microcipare",
              description: "Desfășurăm campanii de microcipare și vaccinare antirabică gratuită pentru câinii din Sectorul 1"
            }
          ].map((project, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="bg-accent-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                {project.icon}
              </div>
              <h3 className="font-bold text-primary mb-3">{project.title}</h3>
              <p className="text-gray-600 text-sm">{project.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Image Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-lg shadow-md"
          >
            <img
              src="/small-girl-dog.png"
              alt="Copil cu câine - educație și empatie"
              className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300">
              <div className="absolute bottom-4 left-4 text-white">
                <p className="font-semibold">Educație și empatie</p>
                <p className="text-sm">Copiii învață să iubească animalele</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative overflow-hidden rounded-lg shadow-md"
          >
            <img
              src="/hospital-picture.png"
              alt="Îngrijire veterinară profesională"
              className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300">
              <div className="absolute bottom-4 left-4 text-white">
                <p className="font-semibold">Îngrijire veterinară</p>
                <p className="text-sm">Parteneriat cu specialiștii</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
          >
            <img
              src="/discussion-girls.jpg"
              alt="Discuții despre adopție și educație"
              className="w-full h-64 object-cover hover:scale-110 transition-transform duration-500 filter hover:brightness-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 hover:opacity-100 transition-all duration-300">
              <div className="absolute bottom-6 left-6 text-white transform translate-y-2 hover:translate-y-0 transition-transform duration-300">
                <p className="font-bold text-lg mb-1">Educație comunitară</p>
                <p className="text-sm opacity-90">Discuții despre adopție responsabilă</p>
              </div>
            </div>
            <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 opacity-0 hover:opacity-100 transition-opacity duration-300">
              <Users size={20} className="text-white" />
            </div>
          </motion.div>
        </div>
      </Container>

      <div className="py-8"></div>

      {/* Importanța adopției */}
      <div className="bg-primary-100 py-16">
        <Container className="py-0">
          <SectionHeader 
            title="Importanța adopției" 
            subtitle="Învățăm ce înseamnă responsabilitatea"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Heart size={32} className="text-accent" />,
                title: "Învățăm responsabilitatea",
                description: "Adopția unui animal este o decizie care nu trebuie luată cu ușurință, deoarece ne poate marca întreaga viață. Acest gest ne învață ce înseamnă responsabilitatea."
              },
              {
                icon: <Shield size={32} className="text-accent" />,
                title: "Salvăm o viață",
                description: "Adăposturile sunt arhipline, iar mulți câini nu au cunoscut niciodată mângâierea și iubirea unui stăpân. Adoptând un cățel, îi salvăm viața."
              },
              {
                icon: <Users size={32} className="text-accent" />,
                title: "Educăm copiii să fie empatici",
                description: "Copiii învață de la o vârstă fragedă să respecte alte ființe vii, să conștientizeze sentimentele animalelor și să devină mai empatici."
              },
              {
                icon: <CheckCircle size={32} className="text-accent" />,
                title: "Combatem comercializarea",
                description: "Adoptarea combate comercializarea animalelor și înmulțirea ilegală, reducând exploatarea animalelor în vederea împerecherii."
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="font-bold text-primary mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </div>

      <div className="py-8"></div>

      <Container>
        {/* Beneficiile adopției */}
        <SectionHeader 
          title="Beneficiile adopției" 
          subtitle="De ce să alegi adopția în locul cumpărării"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {[
            {
              icon: <CheckCircle size={24} className="text-green-500" />,
              title: "Economisim bani",
              description: "Adoptarea unui animal de companie este adesea mai accesibilă decât achiziționarea unuia de la un crescător. ADOPȚIA E GRATUITĂ!"
            },
            {
              icon: <Heart size={24} className="text-green-500" />,
              title: "Adoptați un animal unic",
              description: "Câinii din adăpost sunt de obicei metiși cu aspect unic și personalități bine definite. În ADĂPOSTUL ODĂI sunt peste 500 de câini unici."
            },
            {
              icon: <Users size={24} className="text-green-500" />,
              title: "Impactul comunitar",
              description: "Prin adopție, contribui la reducerea numărului de animale fără adăpost din comunitatea ta și la creșterea gradului de conștientizare."
            },
            {
              icon: <Shield size={24} className="text-green-500" />,
              title: "Câștigi un prieten pe viață",
              description: "Dragostea unui animal nu are limite, te va însoți la bine și la rău, iar noul tău prieten te va aștepta la ușă în fiecare zi."
            }
          ].map((benefit, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="flex items-center mb-4">
                {benefit.icon}
                <h3 className="font-bold text-primary ml-3">{benefit.title}</h3>
              </div>
              <p className="text-gray-600">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </Container>

      <div className="py-8"></div>

      <Container>
        {/* Procesul de adopție */}
        <SectionHeader 
          title="Procesul de adopție" 
          subtitle="Pașii pentru a adopta un câine de la noi"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {[
            {
              step: "1",
              title: "Cererea de adopție",
              description: "Cererile de adopție se pot introduce imediat ce câinele a fost cazat în adăpostul public. Preluarea se poate face începând cu a 8-a zi lucrătoare."
            },
            {
              step: "2",
              title: "Vizitează-ne în adăpost",
              description: "Dacă vrei să salvezi un cățel și să îi oferi șansa la o viață mai bună, VIZITEAZĂ-NE ÎN ADĂPOSTUL ODĂI."
            },
            {
              step: "3",
              title: "Acte necesare",
              description: "Dovada spațiului, dovada sursei de venit, copia CI, acordul asociației de proprietari (pentru mai mult de 2 câini la bloc)."
            },
            {
              step: "4",
              title: "Adopția gratuită",
              description: "Potrivit Hotărârii nr. 1059/2013, adopția câinilor se face gratuit, pe baza unui contract de adopție."
            },
            {
              step: "5",
              title: "Vizite la domiciliu",
              description: "Păstrăm legătura cu adoptatorii prin materiale foto/video lunar și vizite la domiciliu cel puțin o dată pe an."
            },
            {
              step: "6",
              title: "Suport post-adopție",
              description: "Oferim sprijin post-adopție, inclusiv îndrumări privind îngrijirea și formarea animalelor de companie."
            }
          ].map((step, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-lg shadow-md relative"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="bg-accent text-white w-8 h-8 rounded-full flex items-center justify-center font-bold mb-4">
                {step.step}
              </div>
              <h3 className="font-bold text-primary mb-3">{step.title}</h3>
              <p className="text-gray-600 text-sm">{step.description}</p>
            </motion.div>
          ))}
        </div>

        {/* <div className="bg-accent-100 p-6 rounded-lg">
          <div className="flex items-start">
            <Heart size={24} className="text-accent mr-3 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-primary mb-2">REȚINE!</h3>
              <p className="text-gray-700 text-lg">
                <strong>UN CÂINE ADOPTAT ARE NEVOIE DE RĂBDARE!</strong>
              </p>
            </div>
          </div>
        </div> */}

        {/* Adoption Success Image */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-12 text-center"
        >
          <div className="relative inline-block rounded-2xl overflow-hidden shadow-xl">
            <img
              src="./dog-kiss.png"
              alt="Iubire necondiționată - legătura dintre om și câine"
              className="w-full max-w-2xl h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent">
              <div className="absolute bottom-6 left-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Iubire necondiționată</h3>
                <p className="text-lg">Adopția aduce bucurie în viața ta</p>
              </div>
            </div>
          </div>
        </motion.div> */}
      </Container>


    </>
  );
};

export default About; 